
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { AuthService } from '../../../core/services/auth.service';
import { RightSidebarComponent } from "./right-sidebar/right-sidebar.component";
import { RoutesPath } from '../../../shared/constants/routes-path';
import { ApiService } from '../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { TourStep } from '../../../shared/models/tour-step.model';
import { VirtualTourService } from '../../../shared/services/virtual-tour.service';
import { SharedService } from '../../../shared/services/shared.service';
import { Utils } from '../../../Utils/utils';
import { CommonModule, DatePipe } from '@angular/common';
import { FieldValue, UserType } from '../../../shared/constants/services.enum';
import { AccountModel, UserModel } from '../../../shared/models/loginuser.model';
import { SkeletonLoaderComponent } from "../../../shared/components/skeleton-loader/skeleton-loader.component";
import { ApplicationRequest } from '../../../shared/models/application-request.model';
import { FormGroup } from '@angular/forms';
import { PaymentServiceComponent } from '../services/payment-service/payment-service.component';
import { SrStatus } from '../../../shared/constants/status.enum';
import { EventSliderComponent } from "./event-slider/event-slider.component";
import { environment } from '../../../../environments/environment';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DashboardStaticsComponent } from "./dashboard-statics/dashboard-statics.component";
import { LookupService } from '../../../core/services/lookup.service';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { PlatinumNotificationComponent } from "./platinum-notification/platinum-notification.component";
import { HttpClient, HttpParams } from '@angular/common/http';
import { DcMethodValues } from '../../../shared/constants/dc-method.constants';

interface Application {
  "Description": string,
  "DC Receipt Amount": string,
  "DC Created": Date,
  "SR Number": string,
  "DC Respondent Name": string,
  "CSN": number,
  "DC Current Month SR": string,
  "DC COO Number": null,
  "SR Type": string,
  "DC SR Id": string,
  "DC Member Name (English)": string,
  "DCDownloadURL": string,
  "DC Invoice Number": string,
  "Status": string,
  "DC Invoice Amount": string,
  "SR Sub Type": string,
  "DC Invoice Date": string
}

interface DuplicateIdentityUser {
  name: string;
  userName: string;
}

interface DashboardNotice {
  sessionKey: string;
  imageSrc: string;
  imageAlt: string;
  validUntil: Date;
}
@Component({
  selector: 'app-dashboard',
  imports: [RouterModule, CommonModule, TranslateModule, ...SHARED_COMPONENTS, RightSidebarComponent, DatePipe, SkeletonLoaderComponent,
    PaymentServiceComponent, EventSliderComponent, DashboardStaticsComponent, PlatinumNotificationComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly duplicateIdentitySessionKey = 'dashboardDuplicateIdentityAcknowledged';
  private readonly blockedMemberActionStatuses = ['Cancelled', 'Inactive', 'Hold', 'Suspended', 'Rejected'];
  readonly dashboardNotice: DashboardNotice = {
    sessionKey: 'dashboardSecurityNoticeDismissed20260630',
    imageSrc: '/images/physhing-email-announcment.jpg',
    imageAlt: 'Important security notice: fraudulent email alert',
    validUntil: new Date(2026, 7, 31, 23, 59, 59),
  };
  srStatus = SrStatus;
  applicationRequestPayload: ApplicationRequest = new ApplicationRequest();
  applications: Application[] = [];
  expandServicesList: boolean = false;
  profileData!: UserModel;
  userName = '';
  userEmail = '';
  verifyEmailModal: boolean = false;
  private verifyEmailModalDismissed: boolean = false;
  verifyEmailOtpLoader: boolean = false;
  duplicateIdentityModal: boolean = false;
  showNoticeModal: boolean = false;
  duplicateIdentityAcknowledged: boolean = false;
  duplicateIdentityEmail: string = '';
  duplicateIdentityUsers: DuplicateIdentityUser[] = [];
  showAddBusinessMessage: boolean = true;
  showBenefit: boolean = false;
  ServicesMenu: any = [];

  userAccounts: AccountModel[] = [];
  accountCSN: string = "";
  accountId: string = "";
  lang: any = "en";
  activeMembershipTab: 'members' | 'nonMembers' = 'members';
  pagedAccounts: any[] = []; // accounts for current page
  currentPage = 1;
  itemsPerPage = 5;
  showMemberFilterInput = false;
  memberFilterQuery = '';
  rechargeModal: boolean = false;
  rechargeForm!: FormGroup;
  isPlatinumTheme: boolean = false;
  enableDashboardAlert = environment.mVP1Settings?.enableDashboardAlert ?? true;
  invalidMembershipAccountId: string | null = null;
  isCompanyInformationCompleted = true;
  companyInfoLoading = false;
  private applicationsRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private destroy$ = new Subject<void>();
  private isDashboardRouteActive = false;
  // MVP1 flags controlling Apply Services visibility
  private flags = {
    bgcEnabled: environment.mVP1Settings?.bgcEnabled ?? true,
    sustainabilityEnabled: environment.mVP1Settings?.sustainabilityEnabled ?? true,
  };
  accessReqCount: number = 0;
  paymentAccessLoading = false;
  platinumNoOfTargetApplications: number = 800;
  completedApplicationsCount: number = 0;
  userLoginName: string = "";
  // This method is used to initialize the class instance using router, auth service, api, tour, and shared service
  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private api: ApiService,
    private tour: VirtualTourService,
    private sharedService: SharedService,
    private lookupService: LookupService
  ) {
    if (sessionStorage.getItem("srNumber")) {
      sessionStorage.removeItem("srNumber")
    }
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.lang = localStorage.getItem("selectedLang");
    this.accountCSN = this.sharedService.getCsnAccountId('csn');
    this.isDashboardRouteActive = this.isDashboardRoute(this.router.url);
    this.isPlatinumTheme = this.sharedService.isPlatinumThemeActive();
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        //this condition blocks all non-dashboard routes when profile data is empty, except for unauthorized
        const isDashboard = this.isDashboardRoute(event.url);
        const isUnauthorized = this.isUnauthorizedRoute(event.url);
        if (!isDashboard && !isUnauthorized && this.isProfileDataEmpty()) {
          this.router.navigate([RoutesPath.DASHBOARD], { replaceUrl: true });
          return;
        }
        if (!isDashboard) {
          this.isDashboardRouteActive = false;
          this.clearApplicationsAutoRefresh();
        }
      });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.isDashboardRouteActive = this.isDashboardRoute(event.urlAfterRedirects);
        if (!this.isDashboardRouteActive) {
          this.clearApplicationsAutoRefresh();
        }
      });
    // Filter Apply Services groups based on MVP1 flags
    // this.ServicesMenu = this.buildFromServiceData();

    // this.servicesMenuGroup = ServicesMenu.filter((group: any) => {
    //   if (group.groupName === 'MENU_BUSINESS_GROUP') {
    //     return this.flags.bgcEnabled;
    //   }
    //   if (group.groupName === 'MENU_SUSTAINABILITY') {
    //     return this.flags.sustainabilityEnabled;
    //   }
    //   return group.active;
    // });
    // this.buildFromServiceData();
  }



  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    this.loadPlatinumTargetApplicationCount();
    this.profileData = this.sharedService.getUserProfile();
    if (this.profileData) {
      this.userName = this.lang == 'ar' ? this.profileData['Name Arabic'] : this.profileData['Name English']; //this.profileData['First Name']; //+ " " + this.profileData['Last Name'];
      this.userEmail = this.profileData['EMail Addr'];
      this.userLoginName = this.profileData['Login Name'] ?? this.userLoginName;
      this.userAccounts = Utils.checkIsArray(this.profileData.Account);
      this.getUserData();
    } else {
      this.sharedService.toastMessage('SUCCESS', 'LOGIN_SUCCESS', 'success');
      this.getUserData();
    }
  }

  // This method is used to get user members and non member accounts
  loadingUserData: boolean = false;
  getUserData() {
    this.loadingUserData = true;
    const user = this.authService.getUserClaims();
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get User Profile Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.DASHBOARD,
        "DCChannel": "" //"Mobile"
      }
    }
    let path = ApiEndPoints.GET_USER_PROFILE_RUN_PROCESS // ApiEndPoints.GET_USER_PROFILE_RUN_PROCESS || "GET_USER_PROFILE"
    this.api.post(
      ApiRoot.DCCICPIntegration,
      path, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response?.SiebelMessage.User) {
            this.sharedService.setUserProfile(response?.SiebelMessage.User)
            this.profileData = response?.SiebelMessage.User;
            this.userName = this.lang == 'ar' ? this.profileData['Name Arabic'] : this.profileData['Name English']; //this.profileData['First Name']; //+ " " + this.profileData['Last Name'];
            this.userEmail = this.profileData['EMail Addr'];
            this.userLoginName = this.profileData['Login Name'] ?? this.userLoginName;
            //set default account
            if (this.profileData?.Account) {
              this.userAccounts = Utils.checkIsArray(this.profileData.Account);

              let primaryAccount = this.userAccounts?.find(acc => acc.CSN === this.profileData["Primary CSN"]);

              if (this.sharedService.getPrimaryAccount() && !this.sharedService.getPrimaryAccount()?.CSN) {
                let newAccount = this.userAccounts?.find((acc: any) => acc.Id === this.sharedService.getCsnAccountId('id'));
                if (newAccount?.CSN) {
                  this.accountCSN = newAccount.CSN;
                  this.accountId = newAccount.Id;
                  this.sharedService.setPrimaryAccount(newAccount);
                } else {
                  this.accountCSN = this.sharedService.getPrimaryAccount().CSN;
                  this.accountId = this.sharedService.getCsnAccountId('id');
                }
              } else {
                if (primaryAccount) {
                  this.accountCSN = primaryAccount.CSN;
                  this.accountId = primaryAccount.Id;
                  this.sharedService.setPrimaryAccount(primaryAccount);
                } else {
                  this.sharedService.setPrimaryAccount(this.userAccounts[0]);
                  this.accountCSN = this.userAccounts[0].CSN;
                  this.accountId = this.userAccounts[0].Id;
                }
                this.getAccessRequests();
              }

              this.checkIsMemberPlatinum(primaryAccount);

              this.setDefaultMembershipTab();
              this.updatePagedAccounts();
              // this.getApplications();
              this.getMemberDetail();
              this.fetchPaymentAccessFlags();
              this.checkCompanyInformationStatus();
            }
            this.loadingUserData = false;
            this.initializeDashboardModals();
          }
        },
        error: () => {
          this.loadingUserData = false;
        }
      })
  }

  // This method is used to get adance payment balance
  get getAdancePaymentBalance() {
    const primaryAccount = this.sharedService.getPrimaryAccount();

    if (primaryAccount?.CSN && Array.isArray(this.userAccounts) && this.userAccounts.length > 0) {
      const currentSelectedAccount: any = this.userAccounts.find(
        (account: any) => account.CSN === primaryAccount.CSN
      );
      const paymentProfile = currentSelectedAccount?.["Organization Payment Profile"];
      if (paymentProfile) {
        return Utils.getAdvancePaymentBalance(paymentProfile);
      }
    }

    return 0
  }
  get getBankGuaranteeBalance() {
    const primaryAccount = this.sharedService.getPrimaryAccount();
    if (primaryAccount?.CSN && Array.isArray(this.userAccounts) && this.userAccounts.length > 0) {
      const currentSelectedAccount: any = this.userAccounts.find(
        (account: any) => account.CSN === primaryAccount.CSN
      );
      const bankGuaranteeBal = currentSelectedAccount?.["DC Bank Guarantee Bal"] &&
        currentSelectedAccount?.["DC Bank Guarantee Bal"] > 0 ? currentSelectedAccount?.["DC Bank Guarantee Bal"] : 0;
      return bankGuaranteeBal;
    }

    return 0
  }

  get showWalletBalance(): boolean {
    const accountType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];
    return accountType !== UserType.NON_MEMBER && accountType !== UserType.NON_MEMBER_AR;
  }

  get showAvailableBalance(): boolean {
    if (this.showWalletBalance) {
      return true;
    }
    return this.getAdancePaymentBalance > 0;
  }

  // This method is used to check if account pending
  get isAccountPending() {
    if (this.userAccounts) {
      let pendingAccount = this.userAccounts.find(m => m.CSN == "" && m['Account Type Code'] == UserType.MEMBER);
      if (pendingAccount) {
        return true;
      }
    }
    return false;
  }
  // This method is used to update paged accounts
  updatePagedAccounts() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = this.currentPage * this.itemsPerPage;
    const filteredAccounts = this.sortAccountsByExpiryDateAsc(this.getAccountsByActiveTab());
    this.pagedAccounts = filteredAccounts.slice(start, end);
  }

  // This method is used to total pages
  get totalPages(): number {
    const totalAccounts = this.getAccountsByActiveTab().length;
    return totalAccounts ? Math.ceil(totalAccounts / this.itemsPerPage) : 0;
  }

  // This method is used to change membership tab using tab
  changeMembershipTab(tab: 'members' | 'nonMembers') {
    if (this.activeMembershipTab === tab) {
      return;
    }

    this.activeMembershipTab = tab;
    if (tab === 'members') {
      this.showBenefit = false;
    } else {
      this.showMemberFilterInput = false;
      this.memberFilterQuery = '';
    }
    this.currentPage = 1;
    this.updatePagedAccounts();
  }

  // This method is used to member accounts count
  get memberAccountsCount(): number {
    return this.getAccountsForType(UserType.MEMBER).length;
  }

  // This method is used to non member accounts count
  get nonMemberAccountsCount(): number {
    return this.getAccountsForType(UserType.NON_MEMBER).length;
  }

  // This method is used to get accounts by active tab
  private getAccountsByActiveTab(): AccountModel[] {
    if (this.activeMembershipTab === 'members') {
      return this.filterMembersByQuery(this.getAccountsForType(UserType.MEMBER));
    }
    return this.getAccountsForType(UserType.NON_MEMBER);
  }

  // This method is used to get accounts for type using type
  private getAccountsForType(type: UserType): AccountModel[] {
    return this.userAccounts.filter(
      account => account['Account Type Code'] === type
    );
  }

  // This method is used to filter members using query
  private filterMembersByQuery(accounts: AccountModel[]): AccountModel[] {
    const query = (this.memberFilterQuery || '').trim().toLowerCase();
    if (!query) {
      return accounts;
    }
    return accounts.filter((account) => {
      const csn = String(account?.CSN || '').toLowerCase();
      const nameEn = String(account?.['DC Name English'] || '').toLowerCase();
      const nameAr = String(account?.['DC Name Arabic'] || '').toLowerCase();
      return csn.includes(query) || nameEn.includes(query) || nameAr.includes(query);
    });
  }

  // This method is used to sort accounts by expiry date in ascending order
  private sortAccountsByExpiryDateAsc(accounts: AccountModel[]): AccountModel[] {
    return [...accounts].sort((a, b) => {
      const aTime = this.getExpiryDateSortTime(a?.['DC Expiry Date']);
      const bTime = this.getExpiryDateSortTime(b?.['DC Expiry Date']);
      return aTime - bTime;
    });
  }

  // This method is used to get sortable timestamp from expiry date
  private getExpiryDateSortTime(value: unknown): number {
    const parsed = Utils.parseExpiryDate(value);
    if (!parsed) {
      return Number.MAX_SAFE_INTEGER;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  }

  // This method is used to toggle member filter using event
  onMembersTabDblClick(event: MouseEvent): void {
    if (!event.shiftKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.showMemberFilterInput = !this.showMemberFilterInput;
    if (!this.showMemberFilterInput) {
      this.memberFilterQuery = '';
    } else if (this.activeMembershipTab !== 'members') {
      this.activeMembershipTab = 'members';
    }
    this.currentPage = 1;
    this.updatePagedAccounts();
  }

  // This method is used to handle member filter input using event
  onMemberFilterInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.memberFilterQuery = target?.value || '';
    this.currentPage = 1;
    this.updatePagedAccounts();
  }

  // This method is used to set default membership tab
  private setDefaultMembershipTab() {
    const hasMembers = this.userAccounts.some(
      account => account['Account Type Code'] === UserType.MEMBER
    );
    const hasNonMembers = this.userAccounts.some(
      account => account['Account Type Code'] === UserType.NON_MEMBER
    );
    this.activeMembershipTab = !hasMembers && hasNonMembers ? 'nonMembers' : 'members';
  }
  // This method is used to go to page using page
  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagedAccounts();
  }

  // This method is used to move to the next page
  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  // This method is used to return to the previous page
  prevPage() {
    this.goToPage(this.currentPage - 1);
  }
  // End pagenation for membership


  // This method is used to set primary account using user
  setAsPrimaryAccount(user: AccountModel): boolean {
    const isMemberAccount = user?.['Account Type Code'] === UserType.MEMBER;
    this.invalidMembershipAccountId = null;
    if (isMemberAccount && !user?.CSN) {
      this.invalidMembershipAccountId = user?.Id ?? null;
      //return false;
    }


    this.sharedService.setPrimaryAccount(user);
    this.accountCSN = user.CSN;
    this.accountId = user.Id;
    this.checkIsMemberPlatinum(user);

    this.fetchPaymentAccessFlags();
    // this.clearApplicationsAutoRefresh();
    // this.getApplications();

    this.getMemberDetail();

    this.UpdatePrimaryCSN(user);
    this.checkCompanyInformationStatus();
    return true;
  }

  //this method is used to ppdate Primary CSN in DC system
  UpdatePrimaryCSN(user: AccountModel) {
    if (user.CSN) {
      let requestPayload = {
        "body": {
          "ProcessName": 'DC Update User Primay Account CSN',
          "Account Id": user.Id,
          "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
          "DC Method": "Set Primary"
        }
      }
      this.api.post(
        ApiRoot.DCCICPIntegration,
        ApiEndPoints.RUN_PROCESS,
        requestPayload
      ).subscribe({
        next: (response: any) => {
          if (response['Error Code'] == "" && response['Error Message'] == "") {
            this.sharedService.toastMessage('SUCCESS', 'PRIMARY_ACCOUNT_UPDATED_SUCCESSFULLY', 'success');

            this.getAccessRequests()
          }
        },
        error: (e) => { }
      })
    }
  }
  // This method is used to view profile
  viewProfile() {
    this.router.navigate([RoutesPath.PROFILE])
  }

  loader: boolean = false;
  // This method is used to get applications
  // getApplications(silentRefresh: boolean = false) {
  //   if (silentRefresh && !this.isDashboardRouteActive) {
  //     this.clearApplicationsAutoRefresh();
  //     return;
  //   }
  //   if (!silentRefresh) {
  //     this.loader = true;
  //   }
  //   this.applicationRequestPayload.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();

  //   this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["Account Id"] = this.accountCSN ? "" : this.accountId;
  //   this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"].CSN = this.accountCSN;
  //   this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["SR Number"] = '';
  //   this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["SR Type"] = '';
  //   this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["Status"] = ""//SrStatus.Draft;

  //   this.applicationRequestPayload.body.PageSize = 10;
  //   this.applicationRequestPayload.body.StartRowNum = 0;
  //   this.applicationRequestPayload.body.NewQuery = true;
  //   this.applicationRequestPayload.body.NoDays = 15;
  //   this.applications = [];
  //   this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SR_SUMMARY, this.applicationRequestPayload)
  //     .subscribe({
  //       // This method is used to handle the next callback using response
  //       next: (response: any) => {
  //         if (response?.SiebelMessage) {
  //           if (!silentRefresh) {
  //             this.loader = false;
  //           }
  //           if (response?.SiebelMessage['Service Request']) {              
  //             const serviceRequests = Utils.checkIsArray(
  //               response?.SiebelMessage?.['Service Request']
  //             );
  //             //Filter out the rejected SR
  //             this.applications = serviceRequests?.filter(
  //               (item: any) => item?.Status !== SrStatus.Rejected
  //             ) ?? [];
  //           }
  //           this.handleApplicationsAutoRefreshState();
  //         }
  //       },
  //       // This method is used to handle the error callback
  //       error: () => {
  //         if (!silentRefresh) {
  //           this.loader = false;
  //         }
  //       }
  //     })
  // }

  // private startApplicationsAutoRefresh() {
  //   if (this.applicationsRefreshIntervalId || !this.isDashboardRouteActive) {
  //     return;
  //   }
  //   this.applicationsRefreshIntervalId = setInterval(() => this.getApplications(true), 5000);
  // }

  private clearApplicationsAutoRefresh() {
    if (this.applicationsRefreshIntervalId) {
      clearInterval(this.applicationsRefreshIntervalId);
      this.applicationsRefreshIntervalId = null;
    }
  }

  private setPlatinumTheme(enable: boolean) {
    this.isPlatinumTheme = enable;
    this.sharedService.setPlatinumTheme(enable);
  }

  getIconPath(icon: string): string {
    const fileName = icon?.split('/').pop() ?? icon;
    return this.isPlatinumTheme ? `/icons/platinum/${fileName}` : `/icons/${icon}`;
  }

  ngOnDestroy(): void {
    this.clearApplicationsAutoRefresh();
    this.isDashboardRouteActive = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // private shouldAutoRefreshApplications(): boolean {
  //   if (!this.applications || this.applications.length === 0) {
  //     return false;
  //   }

  //   return this.applications.some((application) =>
  //     (
  //       application.Status === this.srStatus.Paid ||
  //       application.Status === this.srStatus.AwaitingCarnetReturn
  //     ) && !this.isValidHttpsUrl(application["DCDownloadURL"])
  //   );
  // }

  // private handleApplicationsAutoRefreshState() {
  //   if (this.shouldAutoRefreshApplications()) {
  //     this.startApplicationsAutoRefresh();
  //   } else {
  //     this.clearApplicationsAutoRefresh();
  //   }
  // }

  private isDashboardRoute(url: string | undefined | null): boolean {
    if (!url) {
      return false;
    }
    return url.includes('dashboard');
  }
  private isUnauthorizedRoute(url: string | undefined | null): boolean {
    if (!url) return false;
    return url.includes('unauthorized');
  }

  private isProfileDataEmpty(): boolean {
    if (!this.profileData) {
      return true;
    }
    return Object.keys(this.profileData).length === 0;
  }

  // This method is used to goto applications
  // gotoApplications() {
  //   this.router.navigate([RoutesPath.APPLICATIONS]);
  // }  

  // This method is used to manage users using m
  manageUsers(m: AccountModel) {
    if (this.setAsPrimaryAccount(m)) {
      this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.MANAGE_SIGNATORIES}`]);
    }
  }

  // This method is used to dismiss membership not approved message
  dismissMembershipNotApprovedMessage() {
    this.invalidMembershipAccountId = null;
  }
  // This method is used to view membership using m
  viewMembership(m: AccountModel) {
    if (m.CSN) {
      this.router.navigate([`/${RoutesPath.MEMBERSHIP.DETAIL}`, m.CSN]);
    }
  }

  // Open SR detail
  // This method is used to open application using sr
  // openApplication(sr: Application) {
  //   this.sharedService.gotoDetailPage(sr);
  // }

  // This method is used to apply membership
  applyMembership() {
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.MEMBERSHIP.REGISTRATION])
  }
  // This method is used to goto add business
  gotoAddBusiness() {
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.ADD_MORE_BUSINESS])
  }
  // This method is used to apply for services
  // applyForServices() {
  //   this.router.navigate([RoutesPath.SERVICES_LIST]);
  // }

  // This method is used to open welcome modal
  openWelcomeModal() {
    if (this.showNoticeModal) {
      return;
    }
    if (this.sharedService.shouldShowTourModal()) {
      setTimeout(() => {
        this.openTourModal = true;
        this.sharedService.markTourModalShown();
      }, 200);
    }
  }
  openTourModal: boolean = false;
  // This method is used to start tour
  startTour() {
    this.openTourModal = false;
    const steps: TourStep[] = [
      {
        id: 'sidebar',
        target: '#sidebar',
        titleKey: 'TOUR.SIDEBAR_TITLE',
        bodyKey: 'TOUR.SIDEBAR_BODY',
        placement: 'right',
        highlightClass: 'ring-4 ring-dc-sky-300/60',
      },

      {
        id: 'notifications',
        target: '#notifications',
        titleKey: 'TOUR.NOTIF_TITLE',
        bodyKey: 'TOUR.NOTIF_BODY',
        placement: 'top',
      }, {
        id: 'apply-button',
        target: '#apply-button',
        titleKey: 'TOUR.APPLY_TITLE',
        bodyKey: 'TOUR.APPLY_BODY',
        placement: 'bottom',
      },

    ];
    this.tour.start(steps);
  }

  // This method is used to reminde later
  remindeLater() {
    this.openTourModal = false;
    this.sharedService.markTourModalShown();
  }

  // This method is used to get status class using status
  getStatusClass(status: string) {
    return this.sharedService.getStatusClass(status);
  }

  getMemberDisplayStatus(member: any): string {
    return this.sharedService.getMemberDisplayStatus(member);
  }

  canShowMemberActions(member: any): boolean {
    const accountStatus = (member?.['Account Status'] || '').toString().trim();
    return !this.blockedMemberActionStatuses.includes(accountStatus);
  }

  // This method is used to open recarhge modal
  openRechargeModal() {
    if (!this.isPaymentServiceAllowed()) {
      this.sharedService.toastMessage('ERROR', 'SERVICE_NOT_AUTHORIZED', 'error');
      return;
    }
    if (!this.accountCSN) {
      this.sharedService.toastMessage('INFO', 'NO_CSN_NUMBER', 'info');
      return;
    }
    const selectedMember = this.userAccounts.find((account: AccountModel) =>
      account?.CSN === this.accountCSN || account?.Id === this.accountId
    );
    const membershipExpiryDate = String(selectedMember?.['DC Expiry Date'] ?? '').trim();
    if (!membershipExpiryDate) {
      this.sharedService.toastMessage('ERROR', 'RECHARGE_INACTIVE_MEMBERSHIP', 'error');
      return;
    }
    this.rechargeModal = true;
  }
  private fetchPaymentAccessFlags(): void {
    const primaryAccount = this.sharedService.getPrimaryAccount();
    const accountId = primaryAccount?.['Primary Organization Id'] || primaryAccount?.Id || this.accountId;
    if (!accountId || this.paymentAccessLoading) {
      return;
    }
    this.paymentAccessLoading = true;
    const requestPayload = {
      body: {
        ProcessName: 'DC User Favorite Services WF',
        'DC Channel': this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        'DC Method': 'GETFAVOURITES',
        'Account Id': accountId
      }
    };
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.sharedService.setDcUserType(response?.['DC User Type']);
          this.sharedService.setServiceAccessFlags(response || {});
        },
        error: () => {
          this.paymentAccessLoading = false;
        },
        complete: () => {
          this.paymentAccessLoading = false;
        }
      });
  }

  private isPaymentServiceAllowed(): boolean {
    const accountType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];
    if (accountType === UserType.NON_MEMBER || accountType === UserType.NON_MEMBER_AR) {
      return true;
    }
    const flags = this.sharedService.getServiceAccessFlags();
    return flags?.['DC Payment Services Flag'] !== 'N';
  }
  platinumMember: any = "";
  // This method is used to get member detail
  getMemberDetail() {
    this.completedApplicationsCount = 0;
    if (!this.accountCSN) {
      return
    }
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Member Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.DASHBOARD,
        "IOName": "DC Get Acc Details IO",
        "CSN": this.accountCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_MEMBER_DETAIL_BY_CSN, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            let memberData = response?.SiebelMessage?.['DC Account Data Transfer'];
            let pocDetail: any = "";
            if (memberData) {
              const activities = Utils.checkIsArray(memberData["DC Account Activity"]);
              this.sharedService.setPrimaryMemberActivities(activities);
              const isPlatinum = this.isPlatinumFlag(memberData["DC Platinum Member Flag"])
              pocDetail = {
                memberName: this.lang == 'ar' ? memberData['DC Name Arabic'] : memberData['DC Name English'],
                title: memberData['DC First POC Title'],
                manager: memberData['DC First POC Manager'],
                mobile: memberData["DC First POC Phone"] ? memberData["DC First POC Phone"] : memberData['DC First POC Mobile'],
                isPlatinumAccount: isPlatinum ? "Yes" : "No",
                email: memberData['DC First POC Email Addr'],
                emailLink: 'mailto:' + memberData["DC First POC Email Addr"]
              }
              this.setPlatinumTheme(isPlatinum);
            }
            this.platinumMember = pocDetail;
          }
        },
        error: () => {
        }
      })

    this.getMemberCompletedApplicationCount();
  }

  checkIsMemberPlatinum(memberData: any) {
    this.setPlatinumTheme(false);
    let pocDetail: any = "";
    if (memberData) {
      const isPlatinum = this.isPlatinumFlag(memberData["DC Platinum Member Flag"])
      pocDetail = {
        memberName: this.lang == 'ar' ? memberData['DC Name Arabic'] : memberData['DC Name English'],
        title: memberData['DC First POC Title'],
        manager: memberData['DC First POC Manager'],
        mobile: memberData["DC First POC Phone"] ? memberData["DC First POC Phone"] : memberData['DC First POC Mobile'],
        isPlatinumAccount: isPlatinum ? "Yes" : "No",
        email: memberData['DC First POC Email Addr'],
        emailLink: 'mailto:' + memberData["DC First POC Email Addr"]
      }
      this.setPlatinumTheme(isPlatinum);
    }
    this.platinumMember = pocDetail;
  }

  private isPlatinumFlag(flag: unknown): boolean {
    if (flag === null || flag === undefined) {
      return false;
    }
    const normalized = String(flag).trim().toLowerCase();
    return normalized === 'y' || normalized === 'yes' || normalized === 'true';
  }
  // This method is used to goto payment page using application
  // gotoPaymentPage(application: any) {
  //   this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${application["SR Number"]}`]);
  // }

  // isValidHttpsUrl(url: unknown): url is string {
  //   return Utils.checkIsValidURL(url);
  // }

  // This method is used to open download link using url
  // openDownloadLink(url: any): void {
  //   if (!url) {
  //     return;
  //   }
  //   window.open(url, '_blank', 'noopener');
  // }

  // This method is used to get member access request and show as notification
  getAccessRequests() {
    let accReqPayload = {
      "body": {
        "ProcessName": "DC Get User Access Request Count WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.DASHBOARD,
        "CSN": this.accountCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, accReqPayload)
      .subscribe({
        next: (response: any) => {
          if (response["SRCount"]) {
            this.accessReqCount = response.SRCount;
          }
        }
      })
  }
  viewPlatinumBenefit() {
    this.router.navigate([RoutesPath.PLATINUM_MEMBERSHIP_BENEFITS]);
  }
  get isPrimaryUser() {
    return this.sharedService.checkPrimaryUser(this.accountCSN);
  }
  gotoStaticsPage() {
    this.router.navigate([RoutesPath.STATICS]);
  }
  private checkCompanyInformationStatus(): void {
    const accountType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];
    if (accountType === UserType.NON_MEMBER || accountType === UserType.NON_MEMBER_AR) {
      this.isCompanyInformationCompleted = true;
      return;
    }
    const accountId = this.accountId || this.sharedService.getCsnAccountId('id');
    if (!accountId) {
      this.isCompanyInformationCompleted = true;
      return;
    }
    this.companyInfoLoading = true;
    this.sharedService.checkCompanyInformationRequiredFields(accountId).subscribe({
      next: (isComplete: boolean) => {
        this.isCompanyInformationCompleted = isComplete;
        this.companyInfoLoading = false;
      },
      error: () => {
        this.isCompanyInformationCompleted = false;
        this.companyInfoLoading = false;
      },
    });
  }
  gotoCompanyInfo() {
    let path = `${RoutesPath.SERVICE}/${RoutesPath.COMPANY_INFORMATION}`;
    this.router.navigate([path]);
  }
  // Method for check email address verified
  private isEmailVerificationRequired(): boolean {
    const flagRaw = this.profileData?.['DC Email Addr Verified Flg'];
    const flag = (flagRaw ?? '').toString().trim().toUpperCase();
    return flag === '' || flag === 'N';
  }

  private updateVerifyEmailModalState(): void {
    if (this.verifyEmailModalDismissed) {
      return;
    }

    const hasIdentity = !!(this.userLoginName && this.userEmail);
    if (!hasIdentity || !this.isEmailVerificationRequired() || this.duplicateIdentityModal || this.showNoticeModal) {
      this.verifyEmailModal = false;
      return;
    }

    this.startDashboardEmailVerification();
  }

  onVerifyEmailClosed(): void {
    this.verifyEmailModal = false;
    this.verifyEmailModalDismissed = true;
  }

  onVerifyEmailSuccess(): void {
    // Optimistically mark as verified to prevent the modal from reopening in the same session.
    if (this.profileData) {
      (this.profileData as any)['DC Email Addr Verified Flg'] = 'Y';
      this.sharedService.setUserProfile(this.profileData);
    }
    this.verifyEmailModal = false;
    this.verifyEmailModalDismissed = true;
  }
  //End method for verify email

  startDashboardEmailVerification(): void {
    const loginName = this.normalizeEmail(this.userLoginName);
    const emailAddr = this.normalizeEmail(this.userEmail);

    if (!loginName || !emailAddr || this.verifyEmailOtpLoader) {
      return;
    }

    const requestPayload = {
      body: {
        ProcessName: 'DC CP Email Verification Process WF',
        LoginName: loginName,
        EmailAddr: emailAddr,
        ReGenOTPFlg: 'N',
      },
    };

    this.verifyEmailOtpLoader = true;
    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.GENERATE_EMAIL_OTP, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.verifyEmailOtpLoader = false;
          if (this.isOtpSuccessResponse(response)) {
            this.verifyEmailModal = true;
            return;
          }

          this.sharedService.toastMessage(
            'ERROR',
            response?.['Error Message'] || 'TECHNICAL_ERROR',
            'error'
          );
        },
        error: () => {
          this.verifyEmailOtpLoader = false;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
        },
      });
  }

  onDuplicateIdentityAcknowledgeChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.duplicateIdentityAcknowledged = !!target?.checked;
  }

  closeDuplicateIdentityModal(): void {
    if (!this.duplicateIdentityAcknowledged) {
      return;
    }

    this.markDuplicateIdentityAcknowledgedForSession();
    this.duplicateIdentityModal = false;
    this.updateVerifyEmailModalState();
  }

  gotoProfileFromDuplicateIdentity(): void {
    this.duplicateIdentityModal = false;
    this.router.navigate([RoutesPath.PROFILE]);
  }

  private checkLoggedInUserDuplicateEmail(): void {
    if (this.showNoticeModal) {
      return;
    }
    const normalizedEmail = this.normalizeEmail(this.userEmail);
    if (!normalizedEmail) {
      return;
    }

    const params = new HttpParams().set('filter', `emails eq ${normalizedEmail}`);
    this.http
      .get<any>(`${environment.wso2APIURL}scim2/Users`, { params })
      .subscribe({
        next: (response: any) => {
          const totalResults = Number(response?.totalResults || 0);
          if (totalResults <= 1 || this.isDuplicateIdentityAcknowledgedForSession()) {
            this.resetDuplicateIdentityModal();
            this.updateVerifyEmailModalState();
            return;
          }

          this.duplicateIdentityEmail = normalizedEmail;
          this.duplicateIdentityUsers = this.extractDuplicateIdentityUsers(response);
          this.duplicateIdentityAcknowledged = false;
          this.duplicateIdentityModal = this.duplicateIdentityUsers.length > 0;
        },
        error: () => {
          this.resetDuplicateIdentityModal();
          this.updateVerifyEmailModalState();
        },
      });
  }

  private extractDuplicateIdentityUsers(response: any): DuplicateIdentityUser[] {
    const resources = Array.isArray(response?.Resources) ? response.Resources : [];
    const seen = new Set<string>();

    return resources
      .map((resource: any) => {
        const givenName = (resource?.name?.givenName ?? '').toString().trim();
        const familyName = (resource?.name?.familyName ?? '').toString().trim();
        const fullName = `${givenName} ${familyName}`.trim();
        const userName = (resource?.userName ?? '').toString().trim();
        return {
          name: fullName || '-',
          userName: userName || '-',
        };
      })
      .filter((item: DuplicateIdentityUser) => {
        const key = `${item.name}|${item.userName}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  private resetDuplicateIdentityModal(): void {
    this.duplicateIdentityModal = false;
    this.duplicateIdentityAcknowledged = false;
    this.duplicateIdentityEmail = '';
    this.duplicateIdentityUsers = [];
  }

  private isDuplicateIdentityAcknowledgedForSession(): boolean {
    const currentIdentity = this.getDuplicateIdentitySessionValue();
    if (!currentIdentity) {
      return false;
    }

    return sessionStorage.getItem(this.duplicateIdentitySessionKey) === currentIdentity;
  }

  private markDuplicateIdentityAcknowledgedForSession(): void {
    const currentIdentity = this.getDuplicateIdentitySessionValue();
    if (!currentIdentity) {
      return;
    }

    sessionStorage.setItem(this.duplicateIdentitySessionKey, currentIdentity);
  }

  private getDuplicateIdentitySessionValue(): string {
    const loginName = this.normalizeEmail(this.userLoginName);
    const email = this.normalizeEmail(this.userEmail);
    return loginName && email ? `${loginName}|${email}` : '';
  }

  private normalizeEmail(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private initializeDashboardModals(): void {
    if (this.shouldShowDashboardNotice()) {
      this.showNoticeModal = true;
      return;
    }

    this.openWelcomeModal();
    this.checkLoggedInUserDuplicateEmail();
  }

  closeNoticeModal(): void {
    this.showNoticeModal = false;
    sessionStorage.setItem(this.dashboardNotice.sessionKey, 'true');
    this.openWelcomeModal();
    this.checkLoggedInUserDuplicateEmail();
  }

  private shouldShowDashboardNotice(): boolean {
    const now = new Date();
    const isValid = now.getTime() <= this.dashboardNotice.validUntil.getTime();
    const isDismissed = sessionStorage.getItem(this.dashboardNotice.sessionKey) === 'true';
    return isValid && !isDismissed;
  }

  private isOtpSuccessResponse(response: any): boolean {
    const errorCode = response?.['Error Code'];
    const errorMsg = response?.['Error Message'];
    const codeOk = errorCode === 0 || errorCode === '0' || errorCode === '' || errorCode == null;
    const msgOk = errorMsg === '' || errorMsg == null;
    return codeOk && msgOk;
  }

  // This method is used to get platinum target count
  private loadPlatinumTargetApplicationCount(): void {
    this.lookupService.post<any>(LookupKey.LOOKUP_URL, {
      ProcessName: 'DC Get List Of Values WF',
      Type: LookupKey.PLATINIUM_MEM_LIMIT,
      ParentValue: ''
    }).subscribe({
      next: (response: any) => {
        const lovList = Utils.checkIsArray(response?.SiebelMessage?.['List Of Values']);
        const firstEntry = lovList?.[0];
        const parsedValue = this.parseNumericValue(
          firstEntry?.Value ?? firstEntry?.Name ?? firstEntry?.High ?? firstEntry?.Description
        );
        if (parsedValue !== null && parsedValue > 0) {
          this.platinumNoOfTargetApplications = parsedValue;
        }
      },
      error: () => {
      }
    });
  }

  // This method is used to parse value to number
  private parseNumericValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    const rawValue = String(value).trim();
    if (!rawValue) {
      return null;
    }
    const numericText = rawValue.replace(/[^0-9.]/g, '');
    if (!numericText) {
      return null;
    }
    const parsed = Number(numericText);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // This method is used to get member completed application count
  private getMemberCompletedApplicationCount() {
    this.completedApplicationsCount = 0;
    const now = new Date();
    const year = now.getFullYear();
    if (!this.accountCSN) {
      return;
    }

    let requestPayload = {
      "account_number": this.accountCSN,
      "sr_cat_type_cd": "",
      "login": "",
      "Period": "Current year",
      "From_Date": this.formatDate(new Date(year, 0, 1)),
      "To_Date": this.formatDate(new Date(year, 11, 31))
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_MEMBER_SERVICE_COMPLETIONCOUNT, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response?.GetMemberServiceCompletionCountOutput) {
            this.completedApplicationsCount = response?.GetMemberServiceCompletionCountOutput[0]?.SRCount ?? 0;
          }
        }
      })

  }
}
